import { Route, RouteConfig } from 'vue-router';
import { ActionPermissionEnum, PagePermissionEnum } from '@/types/enum/PermissionEnum';
import { ImportedFileStatus } from '@/types/enum/ImportedFileStatus';
import { ImporterTemplate } from '@/types/enum/ImporterTemplate';
import { SourceConfigEnum } from '@/types/enum/SourceConfigEnum';

const importRoute = [
	{
		path: '/imports/paiements',
		name: 'paymentAdd',
		props: { type: 'remuneration' },
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			label: 'router.label.paymentAdd',
			can: 'payment_add',
			navigationConfigurationName: 'remuneration_manual_accessmaster',
			hiddenOnProjectRoute: true,
			params: {
				type: 'remuneration',
				sourceConfig: SourceConfigEnum.ACCESS_MASTER,
				fileStatusDownload: ImportedFileStatus.ORIGINAL,
				template: ImporterTemplate.DEFAULT_TEMPLATE,
				download: ActionPermissionEnum.IMPORT_REMUNERATION_DOWNLOAD
			}
		}
	},
	{
		path: '/imports/patients',
		name: 'patientAdd',
		props: { type: 'patient' },
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			label: 'router.label.patientAdd',
			orderProperty: 9999,
			can: 'direct_import_patient',
			navigationConfigurationName: 'cpam_patient_accessmaster',
			hiddenOnProjectRoute: true,
			params: {
				type: 'patient',
				sourceConfig: SourceConfigEnum.ACCESS_MASTER,
				fileStatusDownload: ImportedFileStatus.ORIGINAL,
				template: ImporterTemplate.DEFAULT_TEMPLATE,
				download: ActionPermissionEnum.IMPORT_PATIENT_DOWNLOAD
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/addressing/:type',
		name: 'addressingAdd',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_PCR, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'addressing-information',
			isConfigurationMandatory: true,
			params: {
				type: 'addressing-information',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.PCR_DOWNLOAD,
				delete: ActionPermissionEnum.PCR_DELETE,
				fileStatusDownload: ImportedFileStatus.VALIDATED,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	},
	{
		path: '/imports/depot-fichier/:type/administration',
		name: 'fileStorageAdmin',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			label: 'router.label.fileStorageAdministration',
			can: 'import_administration',
			navigationConfigurationName: 'file_storage_accessmaster',
			hiddenOnProjectRoute: true,
			params: {
				type: 'stored_file',
				sourceConfig: SourceConfigEnum.ACCESS_MASTER,
				download: ActionPermissionEnum.FILE_STORAGE_DOWNLOAD,
				delete: ActionPermissionEnum.FILE_STORAGE_DELETE,
				fileStatusDownload: ImportedFileStatus.ORIGINAL,
				template: ImporterTemplate.VERSION_POPUP
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/depot-fichier/:type/',
		name: 'fileStorage',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_STORED_FILE_LIST, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'file-storage',
			isConfigurationMandatory: true,
			params: {
				type: 'stored_file',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.FILE_STORAGE_DOWNLOAD,
				delete: ActionPermissionEnum.FILE_STORAGE_DELETE,
				fileStatusDownload: ImportedFileStatus.ORIGINAL,
				template: ImporterTemplate.VERSION_POPUP
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/facturation/:type',
		name: 'projectImport',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_LIST, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
			navigationConfigurationName: 'billing-information',
			isConfigurationMandatory: true,
			params: {
				type: 'billing-information',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.IMPORT_DOWNLOAD,
				delete: ActionPermissionEnum.IMPORT_DELETE,
				fileStatusDownload: ImportedFileStatus.VALIDATED,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/facturation/:type',
		name: 'projectImport2',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_LIST, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'billing-information-without-nomenclature',
			isConfigurationMandatory: true,
			params: {
				type: 'billing-information-without-nomenclature',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.IMPORT_DOWNLOAD,
				delete: ActionPermissionEnum.IMPORT_DELETE,
				fileStatusDownload: ImportedFileStatus.VALIDATED,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/contact/:type',
		name: 'genericImportContact',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_CONTACT, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'generic-import-contact',
			isConfigurationMandatory: true,
			params: {
				type: 'generic-import-contact',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.CONTACT_DOWNLOAD,
				delete: ActionPermissionEnum.CONTACT_DELETE,
				fileStatusDownload: ImportedFileStatus.VALIDATED,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/actor/:type',
		name: 'genericImportActor',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_ACTOR, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'generic-import-actor',
			isConfigurationMandatory: true,
			params: {
				type: 'generic-import-actor',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				download: ActionPermissionEnum.ACTOR_IMPORT_DOWNLOAD,
				delete: ActionPermissionEnum.IMPORT_DELETE,
				fileStatusDownload: ImportedFileStatus.VALIDATED,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	},
	{
		path: '/projet/:projectToken/imports/depot-fichier-controle/:type',
		name: 'genericImportControlFile',
		props: true,
		component: () => import(
			'../../views/GenericImport.vue'
		),
		meta: {
			can: (to: Route, from: Route, can: any) => {
				return (can(PagePermissionEnum.IMPORT_CONTROL_FILE, {
					token: to.params.projectToken,
					type: 'Project'
				}));
			},
			navigationConfigurationName: 'generic-import-control-file',
			isConfigurationMandatory: true,
			params: {
				type: 'generic-import-control-file',
				sourceConfig: SourceConfigEnum.FUNCTIONALITY_CONFIGURATION,
				delete: ActionPermissionEnum.IMPORT_CONTROL_FILE_DELETE,
				download: ActionPermissionEnum.IMPORT_CONTROL_FILE_DOWNLOAD,
				fileStatusDownload: ImportedFileStatus.ORIGINAL,
				template: ImporterTemplate.DEFAULT_TEMPLATE
			}
		}
	}
] as RouteConfig[];

export default importRoute;
